<template>
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
    <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
      <span>📊</span>
      <span>依恋类型分析</span>
    </h3>
    
    <div class="radar-chart-wrapper mb-4">
      <svg :width="svgSize" :height="svgSize" :viewBox="`0 0 ${svgSize} ${svgSize}`" class="radar-svg">
        <!-- 背景网格 -->
        <g v-for="level in 5" :key="'grid-' + level">
          <polygon
            :points="getPolygonPoints(level * 20)"
            fill="none"
            :stroke="level === 5 ? '#E5E7EB' : '#F3F4F6'"
            stroke-width="1"
          />
        </g>

        <!-- 坐标轴 -->
        <g v-for="(type, index) in attachmentTypes" :key="'axis-' + index">
          <line
            :x1="center"
            :y1="center"
            :x2="getPoint(index, 100).x"
            :y2="getPoint(index, 100).y"
            stroke="#D1D5DB"
            stroke-width="1"
          />
        </g>

        <!-- 数据区域 -->
        <polygon
          :points="dataPolygonPoints"
          fill="rgba(255, 51, 102, 0.2)"
          stroke="#FF3366"
          stroke-width="3"
          fill-opacity="0.3"
          class="transition-all duration-500"
        />

        <!-- 数据点 -->
        <g v-for="(type, index) in attachmentTypes" :key="'point-' + index">
          <circle
            :cx="getPoint(index, attachmentScores[type.key]).x"
            :cy="getPoint(index, attachmentScores[type.key]).y"
            r="6"
            fill="#FF3366"
            class="transition-all duration-500"
          />
        </g>

        <!-- 类型标签 -->
        <g v-for="(type, index) in attachmentTypes" :key="'label-' + index">
          <text
            :x="getLabelPoint(index).x"
            :y="getLabelPoint(index).y"
            text-anchor="middle"
            dominant-baseline="middle"
            class="text-sm font-semibold fill-gray-700"
          >
            {{ type.name }}
          </text>
          <text
            :x="getLabelPoint(index).x"
            :y="getLabelPoint(index).y + 16"
            text-anchor="middle"
            dominant-baseline="middle"
            class="text-xs fill-gray-500"
          >
            {{ Math.round(attachmentScores[type.key]) }}分
          </text>
        </g>
      </svg>
    </div>

    <!-- 主导类型显示 -->
    <div class="text-center mt-4 p-4 bg-gradient-to-r from-psychology-primary-10 to-psychology-secondary-10 rounded-lg">
      <p class="text-sm text-gray-700">
        <strong class="text-psychology-primary">主导类型:</strong> 
        <span class="text-psychology-primary font-semibold">{{ dominantType }}</span> 
        <span class="text-gray-500">+</span> 
        <span class="text-psychology-primary/70 font-semibold">{{ secondaryType }}</span>
      </p>
    </div>
  </div>
</template>

<script>
import { calculateAttachmentScores } from '@/data/personalityTypes'

export default {
  name: 'AttachmentRadarChart',
  props: {
    scores: {
      type: Object,
      required: true,
      validator: (value) => {
        return value.MAT !== undefined && 
               value.CAR !== undefined && 
               value.GUI !== undefined && 
               value.IND !== undefined
      }
    },
    size: {
      type: Number,
      default: 400
    }
  },
  data() {
    return {
      attachmentTypes: [
        { key: '安全-自主平衡型', name: '安全-自主' },
        { key: '探索-依赖型', name: '探索-依赖' },
        { key: '独立-回避型', name: '独立-回避' },
        { key: '矛盾-动态型', name: '矛盾-动态' }
      ]
    }
  },
  computed: {
    // 响应式 SVG 尺寸：移动端自适应，桌面端使用默认值
    svgSize() {
      if (typeof window !== 'undefined') {
        const isMobile = window.innerWidth <= 768
        if (isMobile) {
          // 移动端：使用视口宽度减去 padding
          const maxWidth = Math.min(window.innerWidth - 80, 350)
          return Math.max(280, maxWidth)
        }
      }
      return this.size
    },
    center() {
      return this.svgSize / 2
    },
    radius() {
      return this.svgSize / 2 - 60
    },
    labelDistance() {
      // 移动端标签距离更近，避免超出
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
      return isMobile ? 30 : 40
    },
    attachmentScores() {
      return calculateAttachmentScores(this.scores)
    },
    dataPolygonPoints() {
      return this.attachmentTypes
        .map((type, index) => {
          const score = this.attachmentScores[type.key] || 0
          const point = this.getPoint(index, score)
          return `${point.x},${point.y}`
        })
        .join(' ')
    },
    dominantType() {
      const sorted = Object.entries(this.attachmentScores)
        .sort((a, b) => b[1] - a[1])
      return sorted[0]?.[0] || ''
    },
    secondaryType() {
      const sorted = Object.entries(this.attachmentScores)
        .sort((a, b) => b[1] - a[1])
      return sorted[1]?.[0] || ''
    }
  },
  methods: {
    getPoint(index, value) {
      const angle = (Math.PI * 2 * index) / this.attachmentTypes.length - Math.PI / 2
      const distance = (value / 100) * this.radius
      return {
        x: this.center + Math.cos(angle) * distance,
        y: this.center + Math.sin(angle) * distance
      }
    },
    getLabelPoint(index) {
      const angle = (Math.PI * 2 * index) / this.attachmentTypes.length - Math.PI / 2
      const distance = this.radius + this.labelDistance
      return {
        x: this.center + Math.cos(angle) * distance,
        y: this.center + Math.sin(angle) * distance
      }
    },
    getPolygonPoints(value) {
      return this.attachmentTypes
        .map((_, index) => {
          const point = this.getPoint(index, value)
          return `${point.x},${point.y}`
        })
        .join(' ')
    },
    handleResize() {
      // 触发响应式更新
      this.$forceUpdate()
    }
  },
  mounted() {
    // 监听窗口大小变化，更新 SVG 尺寸
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize)
      this.handleResize()
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize)
    }
  }
}
</script>

<style scoped>
.radar-chart-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  padding: 0;
}

.radar-svg {
  width: 100%;
  height: auto;
  max-width: 400px;
  overflow: visible;
}

@media (max-width: 768px) {
  .radar-chart-wrapper {
    padding: 0 8px;
  }
  
  .radar-svg {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .radar-chart-wrapper {
    padding: 0 4px;
  }
}
</style>

